import { TimeUnitTypes } from "../timeUnitTypes";
import { ENTITY_ICON } from "../listUtils";
import { ETaskStatus, TaskTypes } from "../../../data/schemas";

export const TASK_STATES = {
  PLANNED: "TASK.TITLE.STATUS.PLANNED",
  IN_PROGRESS: "TASK.TITLE.STATUS.IN_PROGRESS",
  STUCK: "TASK.TITLE.STATUS.STUCK",
  DONE: "TASK.TITLE.STATUS.DONE",
};

export const getTaskRelatedToAppearance = (relatedToParameter) => {
  if (relatedToParameter) {
    switch (true) {
      case relatedToParameter.startsWith("PROJECT"):
        return ENTITY_ICON.PROJECT;
      case relatedToParameter.startsWith("PRODUCT"):
        return ENTITY_ICON.PRODUCT;
      case relatedToParameter.startsWith("LEAD"):
        return ENTITY_ICON.LEAD;
      case relatedToParameter.startsWith("USER"):
        return ENTITY_ICON.USER;
      default:
        return;
    }
  }
};

export const isCurrentTimeBefore = (dueDate) => {
  return new Date() < new Date(dueDate);
};

export const TIME_MODES = {
  REMAINING: "REMAINING",
  NOTIFICATION: "NOTIFICATION",
};

export const getTimeAgoStringForIntl = (
  messageId,
  value,
  unit,
  intl,
  mode = TIME_MODES.REMAINING
) => {
  switch (mode) {
    case TIME_MODES.REMAINING:
      return intl.formatMessage(
        { id: `${messageId || "TIMEAGO.LEFT"}.${unit.toUpperCase()}` },
        { time: value }
      );
    case TIME_MODES.NOTIFICATION:
      // This method returns "In X <unit>" or "X <unit> ago" whether value is positive or not.
      return intl.formatRelativeTime(-value, unit, { numeric: "auto" });
    default:
      return "";
  }
};

export function getTimeAgoCaption(task, value, unit, intl) {
  if (task.taskType === TaskTypes.CUSTOM) {
    if (task.status !== ETaskStatus.DONE && isCurrentTimeBefore(task.dueDate)) {
      return getTimeAgoStringForIntl(undefined, value, unit, intl);
    }
    if (task.status !== ETaskStatus.DONE && !isCurrentTimeBefore(task.dueDate)) {
      return getTimeAgoStringForIntl("TIMEAGO.OVERDUE.SINCE", value, unit, intl);
    }
  }

  // default / unhandled case(s)
  return intl.formatNumber(value, { style: "unit", unit, unitDisplay: "long" });
}

export const getDesiredDate = (task) => {
  if (
    task.status === ETaskStatus.DONE &&
    !!task.doneDate &&
    new Date(task["dueDate"]) < new Date(task["doneDate"])
  ) {
    return task.dueDate;
  }

  if (task.status === ETaskStatus.DONE && !!task.doneDate) {
    return task.doneDate;
  }

  if (task.status !== ETaskStatus.DONE && task.dueDate) {
    return task.dueDate;
  }
};

export const isShowTimeAgo = (task) => {
  if (task.status === ETaskStatus.DONE && new Date(task["dueDate"]) < new Date(task["doneDate"])) {
    return true;
  }
  if (task.status === ETaskStatus.DONE) {
    return false;
  }

  if (task.taskType !== TaskTypes.CUSTOM && task.status === ETaskStatus.DONE && task.doneDate) {
    return false;
  }

  if (task.status !== ETaskStatus.DONE && task.dueDate) {
    return true;
  }

  if (task.taskType === TaskTypes.CUSTOM && task.status === ETaskStatus.DONE && !!task.doneDate) {
    return true;
  }

  if (!task.dueDate) {
    return false;
  }

  // default / unhandled case(s)
  return false;
};

export const getTimeDiffNowInSeconds = (otherDate) => {
  //if minus -> other date in future
  //if plus -> other date in past
  return new Date(otherDate).getTime() - new Date().getTime();
};

export const getColorForTimeAgo = (task, layoutProps) => {
  const tdDueDate = getTimeDiffNowInSeconds(task.dueDate);

  if (task.status !== ETaskStatus.DONE && isCurrentTimeBefore(task.dueDate)) {
    //less than 3 days
    if (tdDueDate <= 3 * TimeUnitTypes.DAY.value)
      return { color: layoutProps.colorsThemeBaseWarning };

    //more than 3 days
    if (tdDueDate > 3 * TimeUnitTypes.DAY.value)
      return { color: layoutProps.colorsThemeBaseSuccess };
  }
  if (task.status !== ETaskStatus.DONE && !isCurrentTimeBefore(task.dueDate)) {
    // less than 0 -> overdue
    if (tdDueDate < 0) return { color: layoutProps.colorsThemeBaseDanger };
  }
  if (task.status === ETaskStatus.DONE && new Date(task?.dueDate) < new Date(task?.doneDate)) {
    return { color: layoutProps.colorsThemeBaseDanger };
  }

  return { color: layoutProps.colorsGrayGray700 };
};

export const getColorForTimeAgoIcon = (task, layoutProps) => {
  const tdDueDate = getTimeDiffNowInSeconds(task.dueDate);

  if (task.status !== ETaskStatus.DONE && isCurrentTimeBefore(task.dueDate)) {
    //less than 4 days
    if (tdDueDate <= 3 * TimeUnitTypes.DAY.value) return layoutProps.colorsThemeBaseWarning;

    //more than 3 days
    if (tdDueDate > 3 * TimeUnitTypes.DAY.value) return layoutProps.colorsThemeBaseSuccess;
  }
  if (task.status !== ETaskStatus.DONE && !isCurrentTimeBefore(task.dueDate)) {
    // less than 0 -> overdue -> red
    if (tdDueDate < 0) return layoutProps.colorsThemeBaseDanger;
  }
  if (task.status === ETaskStatus.DONE && new Date(task?.dueDate) < new Date(task?.doneDate)) {
    return layoutProps.colorsThemeBaseDanger;
  }

  return layoutProps.colorsGrayGray700;
};

export function getCurrentDateMidnight() {
  let currentDateTime = new Date();
  currentDateTime.setHours(0);
  currentDateTime.setMinutes(0);
  currentDateTime.setSeconds(0);
  return currentDateTime;
}

export function getMessageIds(text) {
  const regexMessageId = new RegExp("[A-Z._]+", "g");
  return text ? Array.from(text.matchAll(regexMessageId)) : [];
}

export const getTaskText = (intl, val, attachments /*, params*/) => {
  /*
  params =
    attachments && attachments.length > 0
      ? {
          friendlyName: attachments && attachments.length > 0 ? attachments?.[0].name : "unknown",
          ...params,
        }
      : { ...params };

  if (!val) {
    return "";
  }
  if (intl.messages[val] && params) {
    return intl.formatMessage({ id: val }, { ...params });
  }
  */
  const params = {
    friendlyName: attachments?.[0]?.name || attachments?.friendlyName || "",
  };

  if (!val) {
    return "";
  }

  if (intl.messages[val] && params) {
    return intl.formatMessage({ id: val }, { ...params });
  }

  const messages = getMessageIds(val);

  if (messages && Array.isArray(messages) && messages.length > 0) {
    for (let i = 0; i < messages.length; i++) {
      const currId = messages?.[i]?.[0];
      if (currId.length > 0 && intl.messages[currId]) {
        val = val.replace(currId, intl.formatMessage({ id: currId }, { ...params }));
      }
    }
  }
  return val || "";
};
